import { Box, Typography, Button, Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded'

import { useIsDesktop } from '../../../utils'
import { ARTICLE_DATE_TYPE } from '../../../types/model'
import { Article } from '../../../types/domain'
import texts from '../../texts'
import { ArticleCard } from '../ArticleCard'
import SkeletonsList from './SkeletonsList'
import styles from './articlesList.styles'

const ShowMoreButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Box sx={styles.showMoreButton}>
      <Button id='show-more' color='secondary' onClick={onClick} fullWidth>
        {texts.getShowMoreLabel()}
      </Button>
    </Box>
  )
}

// PAYPER-1123: mismo layout para "sin resultados" y "error", pero con
// icono/color propios para que no se confundan visualmente.
const EmptyState = ({
  text,
  isError = false,
}: {
  text: string
  isError?: boolean
}) => {
  const Icon = isError ? ErrorOutlineRoundedIcon : SearchOffRoundedIcon
  return (
    <Box sx={styles.emptyState}>
      <Box sx={[styles.emptyStateIcon, isError && styles.emptyStateIconError]}>
        <Icon sx={{ fontSize: '2.5em' }} />
      </Box>
      <Typography sx={styles.emptyStateText}>{text}</Typography>
    </Box>
  )
}

export interface ArticlesListProps {
  isFetching?: boolean
  articles: Article[]
  error?: boolean
  articleDateType?: ARTICLE_DATE_TYPE
  fetchNewPage?: () => void
  onClickArticle: (a: Article) => void
  onClickDelete?: (article: Article) => void
  name?: string
  sx?: SystemStyleObject<Theme>
  errorLabel?: string
  noResultsLabel?: string
  isCompact?: boolean
}

const ArticlesList = ({
  isFetching = false,
  articles = [],
  error = false,
  articleDateType,
  fetchNewPage,
  onClickArticle,
  onClickDelete,
  name,
  sx = {},
  errorLabel,
  noResultsLabel,
  isCompact = false,
}: ArticlesListProps) => {
  const isDesktop = useIsDesktop()

  const articlesListStyles = isDesktop
    ? styles.articlesListDesktop
    : styles.articlesListMobile

  return (
    <Box sx={[styles.root, sx]}>
      <Box sx={styles.content}>
        {isFetching ? (
          <Box sx={{ ...articlesListStyles, mt: 'var(--spacing-s)' }}>
            <SkeletonsList numberOfArticles={isDesktop ? 12 : 4} />
          </Box>
        ) : error ? (
          <EmptyState text={errorLabel ?? texts.getUnexpectedError()} isError />
        ) : articles.length === 0 ? (
          <EmptyState text={noResultsLabel ?? texts.getNoResultsLabel()} />
        ) : (
          <>
            <Box
              sx={articlesListStyles}
              className={!isCompact ? 'withHighlighted' : undefined}
            >
              {articles.map((article, articleIndex) => {
                // NOTE: because the grid direction is by rows, the highlighted elems are in position 1 & 6
                // On any changes on grid, review styles.articlesListDesktop
                const isHighlighted = isCompact
                  ? false
                  : articleIndex % 8 === 0 || articleIndex % 8 === 5

                return (
                  <ArticleCard
                    variant={isHighlighted ? 'portrait' : 'landscape'}
                    sx={isHighlighted ? styles.highlightedCard : undefined}
                    data={article}
                    dateType={articleDateType}
                    onClick={() => onClickArticle(article)}
                    key={article.articleId}
                    id={
                      'article-' + (name ? name.concat('-') : '') + articleIndex
                    }
                    {...(onClickDelete
                      ? {
                          onClickDelete,
                          cardActionDisabled: true,
                          withoutImage: true,
                        }
                      : {})}
                  />
                )
              })}
            </Box>
            {!!fetchNewPage && <ShowMoreButton onClick={fetchNewPage} />}
          </>
        )}
      </Box>
    </Box>
  )
}

export default ArticlesList
